/** Bottom road section of the LUDOKA game board - PC skin (Green/Numpad keyboard) */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { BOTTOM_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for bottom section (Green)
 * Ordered top to bottom for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'GFINAL',
  'GS6',
  'GC6',
  'GF6',
  'GS5',
  'GC5',
  'GF5',
  'GS4',
  'GC4',
  'GF4',
  'GS3',
  'GC3',
  'GF3',
  'GS2',
  'GC2',
  'GF2',
  'GS1',
  'GC1',
  'GF1'
];

export default function PcBottomRoadSection({
  viewBox = '0 0 268 616',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  flippingPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Bottom road section with green final stretch"
    >
      {/* Dark ground border - base layer */}
      <path
        d="M18.52,567.79v25.58c.19,4.46.85,10.47,3.19,14.29,3.31,5.4,8.14,3.41,11.67,2.39,9.3-2.7,5.72,2.49,27.35,2.26,13.92-.15,13.63-2.27,26.32-2.33,11.46-.05,12.04,1.68,24.12,2.31,14.62.77,15.33-1.69,36.3-2.52,15.12-.6,14.54.67,31.12,0,15.18-.61,14.43-1.61,29.01-2.02,6.46-.18,8.42-.04,29.85,0,.07,0,.14,0,.21,0,6.5.01,11.77-5.26,11.78-11.77l.03-15.62-230.96-12.56Z"
        fill="url(#pcbt_paint0)"
      />

      {/* Gray ground overlay - top layer */}
      <path
        d="M18.52,555.37v25.58c.19,4.46.85,10.47,3.19,14.29,3.31,5.4,8.14,3.41,11.67,2.38,1.15-.34,2.11-.55,2.94-.66,5.86-.83,5.46,3.12,24.41,2.92.52,0,1.03,0,1.51-.03,2-.04,3.69-.14,5.15-.25,6.73-.54,8.91-1.69,16.39-1.98.99-.04,2.07-.06,3.27-.07,1.88,0,3.46.03,4.85.11,7.11.38,9.17,1.67,19.26,2.21,14.25.75,15.28-1.57,34.75-2.46.5-.03,1.02-.05,1.56-.07,1-.04,1.94-.08,2.82-.1,2.2-.06,4.01-.08,5.61-.06,1.1,0,2.09.02,3.03.05,5.33.15,8.84.54,19.66.11,12.77-.51,14.27-1.3,23.1-1.78,1.66-.09,3.59-.16,5.9-.23.16,0,.32,0,.47,0,4.43-.13,6.9-.08,15.93-.03,1.63,0,3.49,0,5.6.02,2.29,0,4.88,0,7.85.02.07,0,.14,0,.21,0,6.51,0,11.79-5.28,11.79-11.8v-15.59s-2.76-.17-2.76-.17l-18.91-1.03-33.35-1.81-28.29-1.54-32.39-1.76-19.94-1.08-95.3-5.18Z"
        fill="url(#pcbt_paint1)"
      />

      {/* Row 1: Green gradient center, gray side squares */}
      <rect
        x="95.8"
        y="117.94"
        width="76.84"
        height="76.84"
        fill="url(#pcbt_paint2)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="102.13" y="124.27" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect x="18.96" y="117.94" width="76.84" height="76.84" fill="#efefef" />
      <rect x="172.64" y="117.94" width="76.84" height="76.84" fill="#efefef" />

      {/* Row 2: Blue accent center, dark gray side squares */}
      <rect
        x="95.8"
        y="194.78"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="102.13" y="201.11" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect x="18.96" y="194.78" width="76.84" height="76.84" fill="#c4c4c4" />
      <rect x="172.64" y="194.78" width="76.84" height="76.84" fill="#c4c4c4" />

      {/* Row 3: Green gradient center, gray side squares */}
      <rect
        x="95.8"
        y="271.62"
        width="76.84"
        height="76.84"
        fill="url(#pcbt_paint3)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="102.13" y="277.95" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect x="18.96" y="271.62" width="76.84" height="76.84" fill="#efefef" />
      <rect x="172.64" y="271.62" width="76.84" height="76.84" fill="#efefef" />

      {/* Row 4: Blue accent center, dark gray side squares */}
      <rect
        x="95.8"
        y="348.46"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="102.13" y="354.8" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect x="18.96" y="348.46" width="76.84" height="76.84" fill="#c4c4c4" />
      <rect x="172.64" y="348.46" width="76.84" height="76.84" fill="#c4c4c4" />

      {/* Row 5: Green gradient center, blue left, gray right */}
      <rect
        x="95.8"
        y="425.3"
        width="76.84"
        height="76.84"
        fill="url(#pcbt_paint4)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="102.13" y="431.64" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect
        x="18.96"
        y="425.3"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="172.64" y="425.3" width="76.84" height="76.84" fill="#efefef" />

      {/* Row 6: Bottom with rounded corners */}
      <path d="M18.96,502.14h76.84v76.84H28.96c-5.52,0-10-4.48-10-10v-66.84h0Z" fill="#c4c4c4" />
      <rect x="95.8" y="502.14" width="76.84" height="76.84" fill="#efefef" />
      <path d="M172.64,502.14h76.84v66.84c0,5.52-4.48,10-10,10h-66.84v-76.84h0Z" fill="#c4c4c4" />

      {/* Star decoration (right side, fourth row) - shadow layer + white top layer */}
      <path
        d="M219.6,377.09l-6.62-11.95c-.64-1.15-2.28-1.2-2.98-.08l-7.23,11.59-13.41,2.6c-1.3.25-1.84,1.8-1,2.81l8.79,10.46-1.67,13.56c-.16,1.31,1.14,2.31,2.37,1.82l12.66-5.13,12.38,5.78c1.2.56,2.55-.37,2.46-1.69l-.97-13.63,9.32-9.99c.9-.97.43-2.54-.85-2.86l-13.26-3.29Z"
        fill="#303030"
      />
      <path
        d="M219.6,375.78l-6.62-11.95c-.64-1.15-2.28-1.2-2.98-.08l-7.23,11.59-13.41,2.6c-1.3.25-1.84,1.8-1,2.81l8.79,10.46-1.67,13.56c-.16,1.31,1.14,2.31,2.37,1.82l12.66-5.13,12.38,5.78c1.2.56,2.55-.37,2.46-1.69l-.97-13.63,9.32-9.99c.9-.97.43-2.54-.85-2.86l-13.26-3.29Z"
        fill="white"
      />

      {/* White center path square in left column row 5 */}
      <rect x="25.29" y="431.64" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

      {/* Keyboard key labels */}
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(235.98 188.7)"
      >
        /
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(153.95 571.72) scale(1.33 1)"
      >
        0
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(235.26 344.35)"
      >
        9
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(235.98 420.24)"
      >
        6
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(235.98 497.32)"
      >
        3
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="10.32"
        transform="translate(235.98 572.65) scale(1.12 1)"
      >
        .
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(81.79 188.69)"
      >
        +
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(82.03 343.27)"
      >
        7
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(65.59 252.68)"
      >
        Num
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(65.59 266.61)"
      >
        Lock
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(237.8 264.78)"
      >
        *
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(82.03 419.87)"
      >
        4
      </text>

      {/* Green gradient triangle/arrow at top */}
      <polygon
        points="134.22 2.68 249.48 117.94 18.96 117.21 134.22 2.68"
        fill="url(#pcbt_paint5)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Render pawns at all 19 positions - from top to bottom for 3D overlap effect */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        1.0,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents,
        flippingPawnIds
      )}

      <defs>
        {/* paint0: Dark gradient for ground border */}
        <linearGradient
          id="pcbt_paint0"
          x1="134"
          y1="596.13"
          x2="134"
          y2="622.82"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1a1a1a" />
          <stop offset="1" stopColor="#000000" />
        </linearGradient>

        {/* paint1: Gray overlay gradient for ground surface */}
        <linearGradient
          id="pcbt_paint1"
          x1="18.51"
          y1="577.69"
          x2="249.45"
          y2="577.69"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#666666" />
          <stop offset="1" stopColor="#333333" />
        </linearGradient>

        {/* paint2: Green gradient for Row 1 center */}
        <linearGradient
          id="pcbt_paint2"
          x1="95.8"
          y1="156.36"
          x2="172.64"
          y2="156.36"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#52ffbd" />
          <stop offset="0.28" stopColor="#4ffbb9" />
          <stop offset="0.49" stopColor="#49f2b0" />
          <stop offset="0.69" stopColor="#3ee19f" />
          <stop offset="0.87" stopColor="#2fca88" />
          <stop offset="1" stopColor="#22b573" />
        </linearGradient>

        {/* paint3: Green gradient for Row 3 center */}
        <linearGradient
          id="pcbt_paint3"
          x1="95.8"
          y1="310.04"
          x2="172.64"
          y2="310.04"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#52ffbd" />
          <stop offset="0.28" stopColor="#4ffbb9" />
          <stop offset="0.49" stopColor="#49f2b0" />
          <stop offset="0.69" stopColor="#3ee19f" />
          <stop offset="0.87" stopColor="#2fca88" />
          <stop offset="1" stopColor="#22b573" />
        </linearGradient>

        {/* paint4: Green gradient for Row 5 center */}
        <linearGradient
          id="pcbt_paint4"
          x1="95.8"
          y1="463.72"
          x2="172.64"
          y2="463.72"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#52ffbd" />
          <stop offset="0.28" stopColor="#4ffbb9" />
          <stop offset="0.49" stopColor="#49f2b0" />
          <stop offset="0.69" stopColor="#3ee19f" />
          <stop offset="0.87" stopColor="#2fca88" />
          <stop offset="1" stopColor="#22b573" />
        </linearGradient>

        {/* paint5: Green gradient for top triangle arrow */}
        <linearGradient
          id="pcbt_paint5"
          x1="18.96"
          y1="60.31"
          x2="249.48"
          y2="60.31"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#52ffbd" />
          <stop offset="0.28" stopColor="#4ffbb9" />
          <stop offset="0.49" stopColor="#49f2b0" />
          <stop offset="0.69" stopColor="#3ee19f" />
          <stop offset="0.87" stopColor="#2fca88" />
          <stop offset="1" stopColor="#22b573" />
        </linearGradient>
      </defs>
    </svg>
  );
}
